import { FC, useState } from 'react';
import axiosInstance from 'apis/axiosInstance';
import { Popover } from '@headlessui/react';
import axios from 'axios';
import DuplicateAppointment from 'components/DuplicateAppointment/DupplicateAppointment';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import PartialPatient from 'components/PartialPatient/PartialPatient';
import { renderToast } from 'components/Toast/Toast';
import useMatchMutate from 'hooks/useMatchMutate';
import {
  getErrorReason,
  getIsWarningIconEnabled,
  getOptionsForAcceptedStatus,
  getOptionsForCancelledStatus,
  getOptionsForNewStatus,
  isWarningIconExistedAfter24Hour,
} from 'utils/common';
import { APPOINTMENT_STATUS, ERROR_REASON } from 'utils/constants';
import { IAppointmentDetail } from 'interfaces/appointments';
import AcceptAppointmentModal from 'components/AcceptAppointmentModal/AcceptAppointmentModal';
import RejectAppointmentModal from 'components/RejectAppointmentModal/RejectAppointmentModal';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';

interface PopoverOptionProps {
  appointment: IAppointmentDetail;
  isAppointmentInThePast?: boolean;
  isIconOptionDisabled?: boolean;
  updateDashboardUI?: () => Promise<void>;
}

const {
  GENERAL_ERROR,
  PATIENT_NEED_ADD_TO_FAMILY,
  WRITE_FAIL,
  INACTIVE_PATIENT,
  DUPLICATE_BOOKING,
} = ERROR_REASON;

const PopoverOption: FC<PopoverOptionProps> = ({
  appointment,
  isAppointmentInThePast,
  isIconOptionDisabled,
  updateDashboardUI,
}) => {
  const errorReason = appointment && getErrorReason(appointment);

  const matchMutate = useMatchMutate();

  const [errorModal, setErrorModal] = useState('');
  const [confirmModalMode, setConfirmModalMode] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const isWarningIconEnabled = getIsWarningIconEnabled(appointment);

  const isExistedAfter24Hour = isWarningIconExistedAfter24Hour(appointment);

  const isIconClickable = isWarningIconEnabled ? isExistedAfter24Hour : true;

  const getOptions = () => {
    const isWarningExistedAfter24Hour =
      isWarningIconEnabled && isExistedAfter24Hour;

    if (isWarningExistedAfter24Hour) {
      return ['Accept', 'Reject'];
    }

    if (appointment.status === 'cancelled' || appointment.isCancelled) {
      return getOptionsForCancelledStatus({ errorReason });
    }

    if (
      (appointment.isAccepted || appointment.isBookedByAutoConfirm) &&
      !appointment.isApproved &&
      errorReason === PATIENT_NEED_ADD_TO_FAMILY
    ) {
      const options = getOptionsForAcceptedStatus({
        errorReason,
      });

      return ['Move to accepted', ...options];
    }

    if (appointment.isAccepted) {
      const options = getOptionsForAcceptedStatus({
        errorReason,
      });

      if (
        [GENERAL_ERROR, WRITE_FAIL, DUPLICATE_BOOKING].includes(errorReason) &&
        appointment.isBookedByAutoConfirm
      ) {
        return ['Accept', ...options];
      }

      return options;
    }

    return getOptionsForNewStatus({
      isAppointmentInThePast: !!isAppointmentInThePast,
      currentStatus: appointment.status,
      errorReason,
    });
  };

  const checkedIfErrorIsTheSame = async () => {
    setIsChecking(true);
    let isErrorTheSame = true;
    try {
      const response = await axiosInstance.get(
        `/appointments/${appointment.id}`
      );

      const errorReason = getErrorReason(response.data);

      if (!errorReason) {
        isErrorTheSame = false;
        await matchMutate(/\/appointments\?[\s\S]+/);
        handleAppointmentAlreadyResolved(false);
      }
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
    setIsChecking(false);
    return isErrorTheSame;
  };

  const handleClickOption = async (option: string) => {
    if (
      option === 'Accept' ||
      option === 'Reject' ||
      option === 'Move to accepted' ||
      option === 'Retry'
    ) {
      return setConfirmModalMode(
        option === 'Move to accepted' ? 'Accept' : option
      );
    }

    const isErrorTheSame = await checkedIfErrorIsTheSame();
    if (!isErrorTheSame) return;

    if (option === 'Modify Appointment') {
      return setErrorModal('timeslot');
    }
    if (option === 'Select matching patient') {
      return setErrorModal('patient');
    }
  };

  const handleAppointmentAlreadyResolved = (isDuplicateResolved: boolean) => {
    const message = (
      <>
        This{' '}
        {isDuplicateResolved
          ? 'duplicate appointment'
          : 'partial patient match'}{' '}
        error for <strong>{appointment.patient.name}</strong> has been resolved.
        Please accept or reject the appointment below.
      </>
    );

    renderToast({ message, type: 'success' });
  };

  const handleRebookRequest = async () => {
    try {
      await axiosInstance.patch(`/bookings/retry/${appointment.id}`);
      renderToast({
        type: 'success',
        message: `Successfully rebook appointment`,
      });
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        renderToast({
          type: 'error',
          message: 'Something went wrong. Please try again later',
        });

        return;
      }

      renderToast({
        type: 'error',
        message: error.message,
      });
    } finally {
      setConfirmModalMode('');

      if (updateDashboardUI) {
        await updateDashboardUI();
      }
    }
  };

  const allowedStatus = isIconOptionDisabled
    ? [APPOINTMENT_STATUS.NEW.KEY, APPOINTMENT_STATUS.ERROR.KEY]
    : [
        APPOINTMENT_STATUS.NEW.KEY,
        APPOINTMENT_STATUS.ERROR.KEY,
        APPOINTMENT_STATUS.ACCEPTED.KEY,
        APPOINTMENT_STATUS.CANCELLED.KEY,
      ];

  const isWarningAlreadyAccepted =
    appointment.status === APPOINTMENT_STATUS.ACCEPTED.KEY &&
    isWarningIconEnabled;

  if (
    !allowedStatus.includes(appointment.status) ||
    !isIconClickable ||
    isWarningAlreadyAccepted
  ) {
    return null;
  }

  const isOptionDisabled = (option: string) =>
    [PATIENT_NEED_ADD_TO_FAMILY, INACTIVE_PATIENT].includes(errorReason) &&
    option === 'Modify Appointment';

  const renderModal = () => {
    if (confirmModalMode === 'Accept') {
      return (
        <AcceptAppointmentModal
          appointmentId={appointment.id}
          onClose={() => setConfirmModalMode('')}
        />
      );
    }

    if (confirmModalMode === 'Retry') {
      return (
        <ConfirmationModal
          title={'Rebook appointment request'}
          description={`Please ensure no appointment already exists in the PMS. To proceed, click the "Yes, retry" button. Alternatively, you can review and take action on each appointment individually.`}
          onClose={() => setConfirmModalMode('')}
          onSubmit={async () => await handleRebookRequest()}
          submitBtnTitle="Yes, retry"
        />
      );
    }

    return (
      <RejectAppointmentModal
        appointmentId={appointment.id}
        onClose={() => setConfirmModalMode('')}
      />
    );
  };

  return (
    <>
      {isChecking && <LoadingScreen />}
      {errorModal === 'timeslot' && (
        <DuplicateAppointment
          id={appointment.id}
          mode={errorReason}
          onClose={() => setErrorModal('')}
          onAppointmentAlreadyResolved={() =>
            handleAppointmentAlreadyResolved(true)
          }
        />
      )}
      {confirmModalMode && renderModal()}
      {errorModal === 'patient' && (
        <PartialPatient
          id={appointment.id}
          onClose={() => setErrorModal('')}
          onAppointmentAlreadyResolved={() =>
            handleAppointmentAlreadyResolved(false)
          }
        />
      )}
      <Popover.Panel
        className={`flex flex-col gap-y-1.6 absolute py-2 -right-10 z-10 bg-white shadow-primary rounded-[0.8rem] mt-0.6 ${
          errorReason &&
          ![GENERAL_ERROR, WRITE_FAIL].includes(errorReason) &&
          !isAppointmentInThePast
            ? 'w-21'
            : 'w-16.5'
        }`}
      >
        {getOptions().map((option) => (
          <Popover.Button
            key={option}
            className="w-full px-2 hover:bg-opacity-10 hover:text-magenta text-left 
             disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-darkest-grey"
            onClick={() => handleClickOption(option)}
            disabled={isOptionDisabled(option)}
          >
            {option}
          </Popover.Button>
        ))}
      </Popover.Panel>
    </>
  );
};

export default PopoverOption;
